import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { NAV_LINKS } from '@/lib/constants'
import { colors } from '@/styles/colors'
import AuditCtaButton from '@/components/ui/AuditCtaButton'
import MobileMenu from './MobileMenu'

interface HeaderProps {
  onMobileMenuToggle?: (isOpen: boolean) => void
}

const HEADER_CTA = 'Contact Us'

export default function Header({ onMobileMenuToggle }: HeaderProps) {
  const [isVisible, setIsVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const [scrollTimeout, setScrollTimeout] = useState<NodeJS.Timeout | null>(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false
    const isMobileWidth = window.innerWidth < 768
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    return isMobileWidth || isMobileUA
  })
  const [activeSection, setActiveSection] = useState('#home')
  const location = useLocation()
  const isHomePage = location.pathname === '/'

  // Filter out "Contact Us" from navigation links for desktop
  const desktopNavLinks = NAV_LINKS.filter(link => link.href !== '#contact')

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const isMobileWidth = window.innerWidth < 768
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      setIsMobile(isMobileWidth || isMobileUA)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Track active section based on scroll position
  useEffect(() => {
    if (!isHomePage) return

    const handleScroll = () => {
      const sections = ['#home', '#process', '#services', '#about', '#contact']
      const scrollPosition = window.scrollY + 200 // Offset for better detection

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.querySelector(sections[i])
        if (section) {
          const sectionTop = (section as HTMLElement).offsetTop
          if (scrollPosition >= sectionTop) {
            setActiveSection(sections[i])
            break
          }
        }
      }
    }

    handleScroll() // Initial check
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => window.removeEventListener('scroll', handleScroll)
  }, [isHomePage])

  // Notify parent component when mobile menu state changes
  useEffect(() => {
    if (onMobileMenuToggle) {
      onMobileMenuToggle(isMobileMenuOpen)
    }
  }, [isMobileMenuOpen, onMobileMenuToggle])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Clear any existing timeout
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }

      // Scrolling up - show navbar immediately
      if (currentScrollY < lastScrollY) {
        setIsVisible(true)
      }
      // Scrolling down - hide navbar after 1000ms
      else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        const timeout = setTimeout(() => {
          setIsVisible(false)
        }, 1000)
        setScrollTimeout(timeout)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
    }
  }, [lastScrollY, scrollTimeout])

  return (
    <>
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      <header
        style={{
          position: isMobile ? 'fixed' : 'absolute',
          width: isMobile ? '100%' : '1265px',
          height: isMobile ? '64px' : '100px',
          left: isMobile ? '0' : 'calc(50% - 1265px/2)',
          top: isVisible ? (isMobile ? '0px' : '39px') : '-120px',
          transform: isMobile ? 'none' : 'none',
          background: 'rgba(0, 0, 0, 0.19)',
          backdropFilter: 'blur(16.85px)',
          WebkitBackdropFilter: 'blur(16.85px)', // Safari support
          borderRadius: isMobile ? '0' : '32px',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: isMobile ? 'space-between' : 'center',
          alignItems: 'center',
          padding: isMobile ? '16px' : '22px',
          gap: '10px',
          zIndex: 1000,
          transition: 'top 0.3s ease-in-out',
        }}
      >
        {/* Logo Section */}
        <Link
          to="/"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: isMobile ? '12px' : '15px',
            width: isMobile ? 'auto' : '219px',
            height: isMobile ? '27px' : '42px',
            textDecoration: 'none',
          }}
        >
          <img
            src="/images/SVGs/logo.svg"
            alt="DIGITREK Logo"
            width={isMobile ? 24 : 40}
            height={isMobile ? 26 : 42}
            style={{ width: isMobile ? '24px' : '40px', height: isMobile ? '26px' : '42px' }}
          />
          <div
            style={{
              fontFamily: 'var(--font-instrument-sans)',
              fontWeight: isMobile ? 400 : 500,
              fontSize: isMobile ? '20px' : '32px',
              lineHeight: isMobile ? '27px' : '39px',
              letterSpacing: '2px',
              color: colors.neutral[95],
            }}
          >
            DIGITREK
          </div>
        </Link>

        {isMobile ? (
          /* Hamburger Menu Button */
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px',
              gap: '6px',
              width: '48px',
              height: '48px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            <div style={{ width: '18px', height: '2px', background: colors.neutral[100], borderRadius: '2px' }} />
            <div style={{ width: '18px', height: '2px', background: colors.neutral[100], borderRadius: '2px' }} />
            <div style={{ width: '18px', height: '2px', background: colors.neutral[100], borderRadius: '2px' }} />
          </button>
        ) : (
          <>
            {/* Spacer */}
            <div style={{ width: '84px', height: '43px', flex: 'none' }} />

            {/* Navigation Links */}
            <nav
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0px',
                gap: '38px',
                width: '548px',
                height: '40px',
                flex: 'none',
              }}
            >
              {desktopNavLinks.map((link) => {
                const isActive = isHomePage && activeSection === link.href

                if (!isHomePage) {
                  return (
                    <Link
                      key={link.href}
                      to={link.href === '#home' ? '/' : `/${link.href}`}
                      className="nav-link-hover"
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '8px',
                        gap: '8px',
                        fontFamily: 'var(--font-instrument-sans)',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '20px',
                        lineHeight: '24px',
                        textDecoration: 'none',
                        color: colors.neutral[80],
                        height: '40px',
                        whiteSpace: 'nowrap',
                        flex: 'none',
                      }}
                    >
                      {link.label}
                    </Link>
                  )
                }

                return (
                  <a
                    key={link.href}
                    href={link.href}
                    className="nav-link-hover"
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '8px',
                      gap: '8px',
                      fontFamily: 'var(--font-instrument-sans)',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '24px',
                      textDecoration: isActive ? 'underline' : 'none',
                      color: isActive ? colors.neutral[100] : colors.neutral[80],
                      height: '40px',
                      whiteSpace: 'nowrap',
                      flex: 'none',
                    }}
                  >
                    {link.label}
                  </a>
                )
              })}
            </nav>

            {/* Spacer */}
            <div style={{ width: '84px', height: '43px', flex: 'none' }} />

            {/* Contact Button */}
            <AuditCtaButton
              href={isHomePage ? '#contact' : undefined}
              to={isHomePage ? undefined : '/#contact'}
              variant="header"
              title={HEADER_CTA}
            >
              {HEADER_CTA}
            </AuditCtaButton>
          </>
        )}
      </header>
    </>
  )
}

